import { alpha, Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { useContext } from 'react';

import { UserContext } from '~/auth/userContext';
import { ON_FILE_ONLY_LABEL } from '~/components/invoices/CreditStatusChip';
import { LiftAITextLink } from '~/components/utils/helpers';
import { type Invoice, InvoiceStatus } from '~/data/schemas/invoice';
import { CARD_SX } from '~/theme';
import { formatCurrency } from '~/utils/format';
import { buildUrlForInvoice } from '~/utils/urlBuilder';

const toNumber = (value: string | number | null | undefined) =>
  value == null || value === '' ? 0 : Number(value);

/** The credits against an invoice, stamped under its decision with the net it leaves. */
export function InvoiceCreditsCard({ invoice }: { invoice: Invoice }) {
  const { currencyCode } = useContext(UserContext);
  if (!invoice.credits.length) return null;

  const approved =
    invoice.status === InvoiceStatus.PartialApproval
      ? toNumber(invoice.reviewedAmount)
      : invoice.status === InvoiceStatus.Approved
        ? toNumber(invoice.proposedAmount)
        : null;
  const counted = invoice.credits
    .filter((credit) => !credit.keepOnFileOnly)
    .reduce((total, credit) => total + toNumber(credit.proposedAmount), 0);

  return (
    <Box
      data-testid="invoice-credits-card"
      sx={{
        ...CARD_SX,
        p: 2,
        borderColor: 'success.light',
        bgcolor: (theme) => alpha(theme.palette.success.main, 0.06),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography variant="subtitle1" fontWeight={600}>
          Credits
        </Typography>
        <Chip label={invoice.credits.length} size="small" color="success" />
      </Stack>
      <Stack spacing={0.75}>
        {invoice.credits.map((credit) => (
          <Stack key={credit.id} direction="row" justifyContent="space-between" spacing={2}>
            <LiftAITextLink
              variant="body2"
              label={`Credit #${credit.number}`}
              href={buildUrlForInvoice(credit.id)}
            />
            {credit.keepOnFileOnly ? (
              <Typography variant="body2" color="text.secondary">
                {ON_FILE_ONLY_LABEL}
              </Typography>
            ) : (
              <Typography variant="body2" fontWeight={600} color="success.dark">
                −{formatCurrency({ value: toNumber(credit.proposedAmount), currencyCode })}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
      {approved !== null && counted > 0 ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Net after credits
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatCurrency({ value: approved - counted, currencyCode })}
            </Typography>
          </Stack>
        </>
      ) : null}
    </Box>
  );
}
